/**
 * A small seeded random source (sfc32), so a draw or a simulated day can be replayed exactly.
 * Never use Math.random in the engine.
 */

export interface Rng {
  /** A float in [0, 1). */
  next(): number;
  /** An integer in [0, n). */
  int(n: number): number;
  /** The array shuffled in place (Fisher-Yates). */
  shuffle<T>(items: T[]): T[];
  /** One item, or undefined for an empty list. */
  pick<T>(items: readonly T[]): T | undefined;
}

export function makeRng(seed: number): Rng {
  // Spread the seed over four words with splitmix-style mixing, then run sfc32.
  let s = seed >>> 0;
  const mix = (): number => {
    s = (s + 0x9e3779b9) >>> 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x85ebca6b) >>> 0;
    z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35) >>> 0;
    return (z ^ (z >>> 16)) >>> 0;
  };
  let a = mix();
  let b = mix();
  let c = mix();
  let d = mix();
  const next = (): number => {
    const t = (((a + b) >>> 0) + d) >>> 0;
    d = (d + 1) >>> 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) >>> 0;
    c = ((c << 21) | (c >>> 11)) >>> 0;
    c = (c + t) >>> 0;
    return t / 4294967296;
  };
  for (let i = 0; i < 12; i += 1) next();
  const rng: Rng = {
    next,
    int: (n) => Math.floor(next() * n),
    shuffle: (items) => {
      for (let i = items.length - 1; i > 0; i -= 1) {
        const j = Math.floor(next() * (i + 1));
        const tmp = items[i] as (typeof items)[number];
        items[i] = items[j] as (typeof items)[number];
        items[j] = tmp;
      }
      return items;
    },
    pick: (items) => (items.length ? items[Math.floor(next() * items.length)] : undefined),
  };
  return rng;
}

/** A seed for one round's draw, derived from the tournament seed so replays match. */
export function roundSeed(seed: number, roundNo: number, attempt = 0): number {
  return (seed * 1000003 + roundNo * 7919 + attempt * 104729) >>> 0;
}
